import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import LessonCard from './LessonCard';
import { fetchLastCompletedLesson } from '../../redux/slices/userSlice';
import { fetchLessonAccess } from '../../redux/slices/lessonAccessSlice';
import '../../scss/components/LessonsPage/LessonsList.scss';
import lessonsData from '../../data/lessons.json';

const LessonsList = ({ lessons, moduleId, moduleTitle, animateIn = true }) => {
    const dispatch = useDispatch();
    const { isAccepted, lastCompletedModule, lastCompletedLessonId } = useSelector(state => state.user);
    const adminMode = useSelector(state => state.adminMode.adminMode);
    const { lessonAccess } = useSelector(state => state.lessonAccess);

    // Загружаем информацию о последнем завершённом уроке и доступах к урокам при монтировании компонента
    useEffect(() => {
        dispatch(fetchLastCompletedLesson());
        dispatch(fetchLessonAccess());
    }, [dispatch]);

    if (!lessons || lessons.length === 0) {
        return null;
    }

    // Получаем последний урок предыдущего модуля
    const getLastLessonIdInModule = (moduleNum) => {
        if (moduleNum < 1) return 0;

        const module = lessonsData.modules.find(m => m.id === moduleNum);
        if (!module || !module.lessons || module.lessons.length === 0) return 0;

        return module.lessons[module.lessons.length - 1].id;
    };

    // Проверяем, нужно ли блокировать урок по различным причинам
    const shouldBlockLesson = (moduleId, lessonId) => {
        const moduleIdNum = Number(moduleId);
        const lessonIdNum = Number(lessonId);

        // Администраторам доступны все уроки
        if (adminMode) {
            return { blocked: false };
        }

        // Проверяем блокировку из БД
        const accessInfo = lessonAccess.find(
            access => access.moduleId === moduleIdNum && access.lessonId === lessonIdNum
        );

        // Если урок специально заблокирован в БД
        if (accessInfo && accessInfo.isLocked) {
            return { blocked: true, reason: 'locked' };
        }

        // Проверка на isAccepted (имеет высший приоритет)
        const isBlockedByAcceptance = !isAccepted && (
            (moduleIdNum === 1 && lessonIdNum > 101) || // В модуле 1 блокируем со 2-го урока
            moduleIdNum > 1 // Во всех модулях с ID > 1 блокируем все уроки
        );

        if (isBlockedByAcceptance) {
            return { blocked: true, reason: 'acceptance' };
        }

        // Для первого урока первого модуля всегда даём доступ
        if (moduleIdNum === 1 && lessonIdNum === 101) {
            return { blocked: false };
        }

        // Находим позицию урока в модуле
        const lessonPosition = lessons.findIndex(lesson => Number(lesson.id) === lessonIdNum);

        // Проверяем доступность урока на основе последнего завершённого урока
        if (moduleIdNum === lastCompletedModule) {
            // Текущий модуль - проверяем, что уроки идут последовательно
            if (lessonIdNum <= lastCompletedLessonId || lessonIdNum === lastCompletedLessonId + 1) {
                return { blocked: false };
            }
            return { blocked: true, reason: 'completion' };
        }
        else if (moduleIdNum < lastCompletedModule) {
            // Предыдущие модули - все уроки доступны
            return { blocked: false };
        }
        else if (moduleIdNum === lastCompletedModule + 1) {
            // Следующий модуль - проверяем, что это первый урок и последний урок предыдущего модуля завершен
            const lastLessonIdInPrevModule = getLastLessonIdInModule(moduleIdNum - 1);

            if (lessonPosition === 0 && lastCompletedLessonId === lastLessonIdInPrevModule) {
                return { blocked: false };
            }
            return { blocked: true, reason: 'completion' };
        }
        else {
            // Модули после следующего - все заблокированы
            return { blocked: true, reason: 'completion' };
        }
    };

    return (
        <div className={`lessons-list-container ${animateIn ? 'animate-in' : 'animate-out'}`}>
            <div className="lessons-grid">
                {lessons.map((lesson, index) => {
                    const blockStatus = shouldBlockLesson(moduleId, lesson.id);
                    return (
                        <div
                            className={`lesson-card-wrapper ${animateIn ? '' : 'animate-out'}`}
                            key={lesson.id}
                            style={{ animationDelay: `${0.25 * (index + 1)}s` }}
                        >
                            <LessonCard
                                lesson={lesson}
                                moduleId={moduleId}
                                index={index + 1}
                                totalLessons={lessons.length}
                                isLocked={blockStatus.blocked}
                                lockReason={blockStatus.reason}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LessonsList; 